import { SESSION_STORAGE_KEYS } from '../consts/sessionStorage';

/**
 * PKCE (RFC 7636) helpers for browser-based OAuth/OIDC flows.
 * - code_verifier: high-entropy cryptographic random string (43..128 chars)
 * - code_challenge: BASE64URL-ENCODE(SHA256(code_verifier))
 */

const PKCE_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

export const PKCE_CODE_CHALLENGE_METHOD = 'S256' as const;

export const generateCodeVerifier = (length = 128): string => {
  if (length < 43 || length > 128) {
    throw new Error('PKCE code_verifier length must be between 43 and 128');
  }

  const bytes = new Uint8Array(length);

  crypto.getRandomValues(bytes);

  let out = '';

  for (let i = 0; i < bytes.length; i++) {
    out += PKCE_ALPHABET[bytes[i] % PKCE_ALPHABET.length];
  }

  return out;
};

const base64UrlEncode = (bytes: Uint8Array): string => {
  // Convert bytes -> binary string -> base64 -> base64url (no padding)
  let binary = '';

  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

export const generateCodeChallengeS256 = async (
  codeVerifier: string,
): Promise<string> => {
  const data = new TextEncoder().encode(codeVerifier);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashBytes = new Uint8Array(hashBuffer);

  return base64UrlEncode(hashBytes);
};

export const setPkceCodeVerifier = (codeVerifier: string): void => {
  sessionStorage.setItem(SESSION_STORAGE_KEYS.OIDC_CODE_VERIFIER, codeVerifier);
};

export const getPkceCodeVerifier = (): string | null => {
  return sessionStorage.getItem(SESSION_STORAGE_KEYS.OIDC_CODE_VERIFIER);
};

export const clearPkceCodeVerifier = (): void => {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.OIDC_CODE_VERIFIER);
};
